import { Box, Checkbox, Chip, Grid, Tooltip, Paper, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { i18n } from '../../translate/i18n';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom';

import { TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ptBR } from 'date-fns/locale';

/**
 * Converte qualquer formato de horário para string HH:MM 
 * @param {any} valor - Pode ser string ISO, string HH:MM, Date ou undefined
 * @returns {string} Formato padronizado HH:MM
 */
const paraHoraMinuto = (valor) => {
  // Caso valor não definido
  if (!valor) return '00:00';
  
  // Caso seja Date
  if (valor instanceof Date) {
    return `${String(valor.getHours()).padStart(2, '0')}:${String(valor.getMinutes()).padStart(2, '0')}`;
  }
  
  // Caso seja string ISO (2023-04-12T14:30:00.000Z)
  if (typeof valor === 'string' && valor.includes('T')) {
    try {
      const [horas, minutos] = valor.split('T')[1].split(':');
      return `${horas}:${minutos}`;
    } catch (e) {
      return '00:00';
    }
  }
  
  // Caso seja string HH:MM
  if (typeof valor === 'string') {
    // Verifica se já está no formato correto
    if (/^\d{2}:\d{2}$/.test(valor)) {
      return valor;
    }
    
    // Tenta extrair horas e minutos de outros formatos de string
    const match = valor.match(/(\d{1,2})[:\s](\d{2})/);
    if (match) {
      const [, horas, minutos] = match;
      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
    }
  }
  
  // Retorna padrão para casos não tratados
  return '00:00';
};

/**
 * Converte string HH:MM para objeto Date
 * @param {string} horaMinuto - String no formato HH:MM
 * @returns {Date} Objeto Date com o horário configurado
 */
const deHoraMinutoParaDate = (horaMinuto) => {
  // Garante que seja uma string
  if (typeof horaMinuto !== 'string') {
    horaMinuto = paraHoraMinuto(horaMinuto);
  }
  
  // Extrai horas e minutos da string
  let horas = 0;
  let minutos = 0;
  
  const formatoValido = /^(\d{1,2}):(\d{2})$/.exec(horaMinuto);
  if (formatoValido) {
    horas = parseInt(formatoValido[1], 10);
    minutos = parseInt(formatoValido[2], 10);
    
    // Verifica valores válidos
    if (horas > 23) horas = 23;
    if (minutos > 59) minutos = 59;
  }
  
  // Cria objeto Date com o horário
  const data = new Date();
  data.setHours(horas);
  data.setMinutes(minutos);
  data.setSeconds(0);
  data.setMilliseconds(0);
  
  return data;
};

const QueueScheduleComponent = ({ values, onChangeValue }) => {
  // Normaliza os valores iniciais para garantir que todos os horários estejam no formato HH:MM
  useEffect(() => {
    let precisaAtualizar = false;
    const valoresNormalizados = { ...values };
    
    // Verifica cada dia e converte os horários para o formato HH:MM se necessário
    Object.entries(values).forEach(([dia, valoresDia]) => {
      const { start, end, intervalStart, intervalEnd } = valoresDia;
      
      // Verifica se algum valor não está no formato HH:MM
      const startFormatado = paraHoraMinuto(start);
      const endFormatado = paraHoraMinuto(end);
      const intervalStartFormatado = paraHoraMinuto(intervalStart);
      const intervalEndFormatado = paraHoraMinuto(intervalEnd);
      
      if (start !== startFormatado || end !== endFormatado || 
          intervalStart !== intervalStartFormatado || intervalEnd !== intervalEndFormatado) {
        precisaAtualizar = true;
        valoresNormalizados[dia] = {
          ...valoresDia,
          start: startFormatado,
          end: endFormatado,
          intervalStart: intervalStartFormatado,
          intervalEnd: intervalEndFormatado
        };
      }
    });
    
    // Atualiza os valores apenas se algum formato precisar ser corrigido
    if (precisaAtualizar) {
      onChangeValue(valoresNormalizados);
    }
  }, []);
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Grid container spacing={2}>
        {Object.entries(values).map(([dia, valoresDia]) => {
          const { open, start, end, interval, intervalStart, intervalEnd } = valoresDia;
          const nomeDia = i18n.t(`queueModal.form.schedules.${dia}`);
          
          return (
            <Grid item xs={12} key={dia}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  mb: 1, 
                  backgroundColor: open ? 'rgba(232, 245, 233, 0.1)' : 'rgba(253, 236, 234, 0.1)',
                  borderLeft: open ? '4px solid #4caf50' : '4px solid #f44336',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Cabeçalho do dia */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: open ? 2 : 0,
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center'
                  }}>
                    <Tooltip
                      title={open ? `Fechar ${nomeDia}` : `Abrir ${nomeDia}`}
                    >
                      <Checkbox
                        checked={open}
                        icon={<NoMeetingRoomIcon color="error" />}
                        checkedIcon={<MeetingRoomIcon color="success" />}
                        onChange={() => {
                          onChangeValue((agendaAnterior) => ({
                            ...agendaAnterior,
                            [dia]: { ...agendaAnterior[dia], open: !open },
                          }));
                        }}
                        sx={{ 
                          '&.MuiCheckbox-root': { 
                            padding: '8px' 
                          }
                        }}
                      />
                    </Tooltip>
                    
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold" 
                      sx={{ mr: 2 }}
                    >
                      {nomeDia}
                    </Typography>
                    
                    <Chip
                      color={open ? 'success' : 'error'}
                      label={open ? 'Aberto' : 'Fechado'}
                      size="small"
                      sx={{ 
                        fontWeight: 'bold',
                        minWidth: '70px'
                      }}
                    />
                  </Box>
                  
                  {/* Checkbox de intervalo - só aparece quando o dia está aberto */}
                  {open && (
                    <Box>
                      <Tooltip title={`${!interval ? 'Habilitar' : 'Desabilitar'} intervalo`}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                            Intervalo
                          </Typography>
                          <Checkbox
                            checked={interval}
                            icon={<AccessTimeIcon />}
                            checkedIcon={<WatchLaterIcon color="primary" />}
                            onChange={() => {
                              onChangeValue((agendaAnterior) => ({
                                ...agendaAnterior,
                                [dia]: { ...agendaAnterior[dia], interval: !interval },
                              }));
                            }}
                            sx={{ 
                              '&.MuiCheckbox-root': { 
                                padding: '4px' 
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                
                {/* Campos de horário - só aparecem quando o dia está aberto */}
                {open && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={interval ? 3 : 6} md={interval ? 3 : 4}>
                      <TimePicker
                        label="Início expediente"
                        ampm={false}
                        value={deHoraMinutoParaDate(start)}
                        onChange={(novoValor) => {
                          onChangeValue((agendaAnterior) => ({
                            ...agendaAnterior,
                            [dia]: { ...agendaAnterior[dia], start: paraHoraMinuto(novoValor) },
                          }));
                        }}
                        slotProps={{ 
                          textField: { 
                            size: 'small', 
                            fullWidth: true,
                            margin: 'dense',
                            InputProps: {
                              readOnly: !open
                            }
                          } 
                        }}
                      />
                    </Grid>
                    
                    {interval && (
                      <Grid item xs={12} sm={3} md={3}>
                        <TimePicker
                          label="Início intervalo"
                          ampm={false}
                          value={deHoraMinutoParaDate(intervalStart)}
                          onChange={(novoValor) => {
                            onChangeValue((agendaAnterior) => ({
                              ...agendaAnterior,
                              [dia]: { ...agendaAnterior[dia], intervalStart: paraHoraMinuto(novoValor) },
                            }));
                          }}
                          slotProps={{ 
                            textField: { 
                              size: 'small', 
                              fullWidth: true,
                              margin: 'dense'
                            } 
                          }}
                        />
                      </Grid>
                    )}
                    
                    {interval && (
                      <Grid item xs={12} sm={3} md={3}>
                        <TimePicker
                          label="Fim intervalo"
                          ampm={false}
                          value={deHoraMinutoParaDate(intervalEnd)}
                          onChange={(novoValor) => {
                            onChangeValue((agendaAnterior) => ({
                              ...agendaAnterior,
                              [dia]: { ...agendaAnterior[dia], intervalEnd: paraHoraMinuto(novoValor) },
                            }));
                          }}
                          slotProps={{ 
                            textField: { 
                              size: 'small', 
                              fullWidth: true,
                              margin: 'dense'
                            } 
                          }}
                        />
                      </Grid>
                    )}
                    
                    <Grid item xs={12} sm={interval ? 3 : 6} md={interval ? 3 : 4}>
                      <TimePicker
                        label="Fim expediente"
                        ampm={false}
                        value={deHoraMinutoParaDate(end)}
                        onChange={(novoValor) => {
                          onChangeValue((agendaAnterior) => ({
                            ...agendaAnterior,
                            [dia]: { ...agendaAnterior[dia], end: paraHoraMinuto(novoValor) },
                          }));
                        }}
                        slotProps={{ 
                          textField: { 
                            size: 'small', 
                            fullWidth: true,
                            margin: 'dense'
                          } 
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mt: 2,
        pt: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MeetingRoomIcon color="success" sx={{ mr: 1 }} />
          <Typography variant="body2">Dia aberto</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NoMeetingRoomIcon color="error" sx={{ mr: 1 }} />
          <Typography variant="body2">Dia fechado</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WatchLaterIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2">Com intervalo</Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default QueueScheduleComponent;